import React, { useCallback, useMemo, useState } from 'react';
import type { ColDef, EditableCallbackParams } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { isoToday } from '../../components/LopHistoryDateFilter';
import {
  getTaseIdxCompositions,
  saveTaseIdxCompositionsBatch,
} from '../../api/taseIdxCompositions';
import type { TaseIdxComposition } from '../../types/TaseIdxComposition';
import { useIndices } from '../../hooks/useIndices';
import { dateColumnProps } from '../../utils/dateColumn';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './TaseIdxCompositionsPage.css';
import { LopDateInput } from '../../components/LopDateInput';

// "הרכב מדדי הבורסה" — the daily RAW composition feed as published by TASE
// itself (dbo.TaseIdxCompositions), keyed by taseIdxId (the exchange's own
// index code), NOT our internal idxId. Composite natural PK
// (dDate, taseIdxId, assetId); idKey synthesis lives in
// src/api/taseIdxCompositions.ts (same recipe as IdxCompositionsHistory).
//
// Filter strip is bespoke (not LopHistoryDateFilter, which is a from/to pair):
// a single date input (default = today) + an index picker where the user
// picks by idxName (INDICES, via useIndices) and the page resolves that row's
// taseIdxId for the actual query param. Both mirror the draft/active dual
// state recipe — only "Fetch" copies draft -> active and re-triggers the grid.
const editableOnAdd = (params: EditableCallbackParams<TaseIdxComposition>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

const columnDefs: ColDef<TaseIdxComposition>[] = [
  {
    field: 'dDate',
    headerName: t('taseIdxCompositions.columns.dDate'),
    width: 140,
    editable: editableOnAdd,
    sort: 'desc',
    ...dateColumnProps<TaseIdxComposition>('dDate'),
  },
  {
    field: 'taseIdxId',
    headerName: t('taseIdxCompositions.columns.taseIdxId'),
    width: 150,
    editable: editableOnAdd,
    cellDataType: 'number',
  },
  {
    field: 'assetId',
    headerName: t('taseIdxCompositions.columns.assetId'),
    width: 150,
    editable: editableOnAdd,
    cellDataType: 'number',
  },
  {
    field: 'assetPercent',
    headerName: t('taseIdxCompositions.columns.assetPercent'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'factor',
    headerName: t('taseIdxCompositions.columns.factor'),
    width: 160,
    editable: true,
    cellDataType: 'number',
  },
];

const newTaseIdxCompositionDefaults = (): Partial<TaseIdxComposition> => ({
  dDate: isoToday(),
  taseIdxId: 0,
  assetId: 0,
  assetPercent: null,
  factor: null,
});

export function TaseIdxCompositionsPage() {
  const { data: indices } = useIndices();

  const [draftDate, setDraftDate] = useState<string>(isoToday);
  const [activeDate, setActiveDate] = useState<string>(draftDate);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);

  // The picker works on INDICES.idxId (unique, stable); the actual query
  // filter is INDICES.taseIdxId, resolved from the picked idxId below.
  const [draftIdxId, setDraftIdxId] = useState<number | null>(null);
  const [activeIdxId, setActiveIdxId] = useState<number | null>(null);

  // Options: index name -> idxId, SKIPPING rows whose taseIdxId is null (they
  // have no meaning for this exchange-fed table).
  const idxOptions = useMemo(
    () =>
      (indices ?? [])
        .filter((i) => i.taseIdxId != null)
        .map((i) => ({ id: i.idxId, name: i.idxName })),
    [indices],
  );

  const taseIdxIdByIdxId = useMemo(() => {
    const m = new Map<number, number>();
    (indices ?? []).forEach((i) => {
      if (i.taseIdxId != null) m.set(i.idxId, i.taseIdxId);
    });
    return m;
  }, [indices]);

  const activeTaseIdxId = useMemo(
    () => (activeIdxId != null ? taseIdxIdByIdxId.get(activeIdxId) : undefined),
    [activeIdxId, taseIdxIdByIdxId],
  );

  const getRows = useCallback(
    () => getTaseIdxCompositions(activeDate || undefined, activeTaseIdxId),
    [activeDate, activeTaseIdxId],
  );

  const saveBatch = useCallback(
    (
      added: TaseIdxComposition[],
      modified: TaseIdxComposition[],
      deletedIdKeys: unknown[],
    ) =>
      saveTaseIdxCompositionsBatch(
        added,
        modified,
        deletedIdKeys as string[],
        activeDate || undefined,
        activeTaseIdxId,
      ),
    [activeDate, activeTaseIdxId],
  );

  function handleFetch() {
    setActiveDate(draftDate);
    setActiveIdxId(draftIdxId);
    setFetched(true);
  }

  return (
    <div className="lop-history-page">
      <div className="tase-idx-compositions__filter">
        <label className="tase-idx-compositions__field">
          <span>{t('taseIdxCompositions.filter.date')}</span>
          <LopDateInput value={draftDate} onChange={setDraftDate} />
        </label>
        <label
          className="tase-idx-compositions__field tase-idx-compositions__field--idx"
          htmlFor="tase-idx-compositions-idx"
        >
          <span>{t('taseIdxCompositions.filter.idx')}</span>
          <LopSearchableSelect
            id="tase-idx-compositions-idx"
            value={draftIdxId}
            options={idxOptions}
            onChange={setDraftIdxId}
            allowNone
            noneLabel={t('taseIdxCompositions.filter.all')}
            placeholder={t('taseIdxCompositions.filter.all')}
          />
        </label>
        <LopButton variant="primary" className="tase-idx-compositions__fetch" onClick={handleFetch}>
          {t('common.fetch')}
        </LopButton>
      </div>
      <LopCrudPage<TaseIdxComposition>
        awaitingFetch={!fetched}
        resourceCode="taseIdxCompositions"
        titleKey="taseIdxCompositions.pageTitle"
        idField="idKey"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newTaseIdxCompositionDefaults}
      />
    </div>
  );
}
